import { useEffect, useCallback, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { fetchCursos } from "Slices/Curso/cursoSlice";
import { fetchPaqueteCursos } from "Slices/PaqueteCursos/paqueteCursosSlice";
import { storageService } from "Services/storage_service";

/**
 * Hook personalizado para obtener cursos y paquetes desde Redux
 * @returns {Object} Objeto con los datos y funciones del hook
 * @returns {Array} returns.cursos - Lista de cursos activos (estado === 1)
 * @returns {Array} returns.paquetes - Lista de paquetes activos (estado === 1)
 * @returns {boolean} returns.isLoading - Indica si se están cargando los datos
 * @returns {Function} returns.refetch - Función para volver a cargar los datos
 */
export const useCursosPaquetes = () => {
  const cookieUser = storageService.getItem("user");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const isFirstRender = useRef(true);

  const cursosData = useSelector((state) => state.curso.cursos, shallowEqual);
  const paquetesData = useSelector(
    (state) => state.paqueteCursos.paquetesCursos,
    shallowEqual
  );

  const isAuthenticated = Boolean(cookieUser?.idUsuario);

  const shouldFetch = useMemo(() => {
    if (cursosData === null || paquetesData === null) {
      return false;
    }

    if (
      (Array.isArray(cursosData) && cursosData.length === 0) ||
      (Array.isArray(paquetesData) && paquetesData.length === 0)
    ) {
      return true;
    }

    return false;
  }, [cursosData, paquetesData]);

  const cursos = useMemo(() => {
    if (!Array.isArray(cursosData)) return [];
    return cursosData.filter((item) => item.estado === 1);
  }, [cursosData]);

  const paquetes = useMemo(() => {
    if (!Array.isArray(paquetesData)) return [];
    return paquetesData.filter((item) => item.estado === 1);
  }, [paquetesData]);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        dispatch(
          fetchCursos({
            codSegmento: "capa",
            ...(isAuthenticated && { idUsuario: cookieUser.idUsuario }),
          })
        ),
        dispatch(
          fetchPaqueteCursos({
            codSegmento: "capa",
            ...(isAuthenticated && { idUsuario: cookieUser.idUsuario }),
          })
        ),
      ]);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (shouldFetch) {
      refetch();
    }
  }, [shouldFetch, refetch]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    refetch();
  }, [isAuthenticated, refetch]);

  return {
    cursos,
    paquetes,
    isLoading,
    refetch,
  };
};
